if (!customElements.get('product-center')) {
  class ProductCenter extends HTMLElement {
    connectedCallback() {
      this.storeDomain = this.dataset.storeDomain || '';
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.fallbackCollectionHandle = this.cleanValue(this.dataset.collectionHandle);
      this.config = this.readConfig();
      this.products = [];
      this.filteredProducts = [];
      this.selectedFilters = new Map();
      this.urlState = this.readUrlState();
      this.selectedCompareProducts = this.readCompareItems();
      this.activeCategory = '';
      this.searchTerm = this.cleanValue(this.config.defaultSearch);
      this.page = 1;
      this.isLoadingProducts = false;
      this.requestId = 0;
      this.searchDebounceTimer = null;
      this.handleProductCenterMenuClick = (event) => this.onProductCenterMenuClick(event);
      this.handlePopState = () => this.applyProductCenterUrl(new URL(window.location.href), { updateHistory: false, shouldScroll: false });
      this.handleResize = () => this.syncFilterPanelHeight();
      this.handleFilterScroll = () => this.updateFilterScrollbar();
      this.handleFilterScrollbarPointerMove = (event) => this.onFilterScrollbarPointerMove(event);
      this.handleFilterScrollbarPointerUp = () => this.onFilterScrollbarPointerUp();
      this.filterPanelResizeObserver = null;
      this.filterScrollbarDrag = null;

      this.nodes = {
        filterPanel: this.querySelector('.product-center__filters'),
        filters: this.querySelector('[data-product-center-filters]'),
        tabs: this.querySelector('[data-product-center-tabs]'),
        grid: this.querySelector('[data-product-center-grid]'),
        summary: this.querySelector('[data-product-center-summary]'),
        status: this.querySelector('[data-product-center-status]'),
        pagination: this.querySelector('[data-product-center-pagination]'),
        compareBar: this.querySelector('[data-product-center-compare-bar]'),
        reset: this.querySelector('[data-product-center-reset]'),
        filterVisibility: this.querySelector('[data-product-center-filter-visibility]'),
        searchForm: this.querySelector('[data-product-center-search-form]'),
        search: this.querySelector('[data-product-center-search]')
      };

      this.manualFilterGroups = this.parseFilterGroups(this.config.filterGroups);
      this.metaobjectFilterGroupConfigs = this.parseMetaobjectFilterGroups(this.config.metaobjectFilterGroups);
      this.cardSpecs = this.parseCardSpecs(this.config.cardSpecs);
      this.cardRibbonField = this.normalizeMetafieldRef(this.config.cardRibbonField || 'custom.subcategory').canonical;
      this.cardTitleField = this.normalizeMetafieldRef(this.config.cardTitleField || 'custom.original_name').canonical;
      this.applyUrlFilters(this.urlState.filters);
      this.metafieldRefs = this.collectMetafieldRefs();
      this.filterGroups = this.manualFilterGroups;
      this.categories = Array.isArray(this.config.categories) ? this.config.categories.filter((item) => item?.label) : [];
      this.activeCategory = this.categories[0]?.value || this.categories[0]?.collectionHandle || this.categories[0]?.label || '';
      this.applyUrlState();
      if (this.nodes.search) this.nodes.search.value = this.searchTerm;

      this.renderFilters();
      this.renderTabs();
      this.renderCompareBar();
      this.ensureFilterScrollbar();
      this.bindEvents();
      this.observeFilterPanelHeight();
      this.loadMetaobjectFilters();
      this.loadProducts();
      this.scrollToProductCenterFromUrlState();
    }

    readConfig() {
      const script = this.querySelector('[data-product-center-config]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    readUrlState(rawSearch = window.location.search) {
      const state = {
        category: '',
        search: '',
        filters: [],
        page: 0
      };

      try {
        this.urlSearchCandidates(rawSearch).forEach((search) => {
          this.mergeUrlSearchState(search, state);
        });
      } catch (_error) {
        return state;
      }

      return state;
    }

    urlSearchCandidates(rawSearch) {
      const candidates = [];
      let current = this.cleanValue(rawSearch);

      for (let index = 0; index < 3 && current; index += 1) {
        const normalized = current.replace(/^\?&/, '?');
        if (!candidates.includes(normalized)) candidates.push(normalized);

        try {
          const decoded = decodeURIComponent(current);
          if (decoded === current) break;
          current = decoded;
        } catch (_error) {
          break;
        }
      }

      return candidates;
    }

    mergeUrlSearchState(search, state) {
      const params = new URLSearchParams(search);
      const category = this.cleanValue(params.get('pc_category') || params.get('pc_collection'));
      const searchTerm = this.cleanValue(params.get('pc_search') || params.get('pc_q'));
      const page = Number.parseInt(params.get('pc_page') || '', 10);

      if (category && !state.category) state.category = category;
      if (searchTerm && !state.search) state.search = searchTerm;
      if (Number.isInteger(page) && page > 0 && !state.page) state.page = page;

      params.getAll('pc_filter').forEach((value) => {
        this.parseUrlFilterValue(value).forEach((filter) => state.filters.push(filter));
      });

      params.forEach((value, key) => {
        const match = key.match(/^pc_filter\[(.+)]$/);
        if (!match) return;
        const field = this.cleanValue(match[1]);
        const option = this.cleanValue(value);
        if (field && option) state.filters.push({ key: field, value: option });
      });
    }

    parseUrlFilterValue(rawValue) {
      return String(rawValue || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => {
          const separatorIndex = this.firstSeparatorIndex(item, [':', '=', '|']);
          if (separatorIndex < 1) return null;
          const key = this.cleanValue(item.slice(0, separatorIndex));
          const value = this.cleanValue(item.slice(separatorIndex + 1));
          return key && value ? { key, value } : null;
        })
        .filter(Boolean);
    }

    firstSeparatorIndex(value, separators) {
      const indexes = separators
        .map((separator) => value.indexOf(separator))
        .filter((index) => index > -1)
        .sort((a, b) => a - b);
      return indexes.length ? indexes[0] : -1;
    }

    applyUrlFilters(filters) {
      if (!Array.isArray(filters)) return;
      filters.forEach((filter) => {
        const ref = this.normalizeMetafieldRef(filter?.key);
        const value = this.cleanValue(filter?.value);
        if (!ref.canonical || !value) return;
        if (!this.selectedFilters.has(ref.canonical)) this.selectedFilters.set(ref.canonical, new Set());
        this.selectedFilters.get(ref.canonical).add(value);
      });
    }

    applyUrlState({ reset = false } = {}) {
      if (reset) {
        this.searchTerm = '';
        this.activeCategory = this.categories[0]?.value || this.categories[0]?.collectionHandle || this.categories[0]?.label || '';
        this.page = 1;
      }

      const search = this.cleanValue(this.urlState?.search);
      if (search) this.searchTerm = search;

      const category = this.matchCategoryValue(this.urlState?.category);
      if (category) this.activeCategory = category;

      const page = Number.parseInt(this.urlState?.page || '', 10);
      if (Number.isInteger(page) && page > 0) this.page = page;
    }

    matchCategoryValue(rawValue) {
      const target = this.cleanValue(rawValue);
      if (!target) return '';

      const targetToken = this.normalizeUrlToken(target);
      const matched = this.categories.find((category) => {
        const value = category.value || category.collectionHandle || category.label;
        const candidates = [
          value,
          category.collectionHandle,
          category.label,
          category.url,
          this.categoryHandleFromUrl(category.url)
        ].map((item) => this.cleanValue(item)).filter(Boolean);

        return candidates.some((candidate) => (
          candidate === target ||
          this.normalizeUrlToken(candidate) === targetToken
        ));
      });

      return matched ? matched.value || matched.collectionHandle || matched.label || '' : '';
    }

    categoryHandleFromUrl(url) {
      const value = this.cleanValue(url);
      if (!value) return '';
      const match = value.match(/\/collections\/([^/?#]+)/);
      return match ? match[1] : '';
    }

    bindEvents() {
      this.nodes.reset?.addEventListener('click', () => {
        this.selectedFilters.clear();
        this.searchTerm = '';
        this.clearSearchDebounce();
        if (this.nodes.search) this.nodes.search.value = '';
        this.page = 1;
        this.renderFilters();
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.loadProducts();
      });

      this.nodes.filterVisibility?.addEventListener('click', () => {
        const collapsed = !this.classList.contains('is-filter-groups-collapsed');
        this.classList.toggle('is-filter-groups-collapsed', collapsed);
        this.querySelectorAll('.product-center__filter-group').forEach((group) => {
          group.classList.toggle('is-collapsed', collapsed);
        });
        this.nodes.filterVisibility.textContent = collapsed ? 'Show Filter' : 'Hide Filter';
        this.nodes.filterVisibility.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        this.syncFilterPanelHeight();
      });

      this.nodes.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.searchTerm = this.cleanValue(this.nodes.search?.value);
        this.clearSearchDebounce();
        this.page = 1;
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.loadProducts();
      });

      this.nodes.search?.addEventListener('input', () => {
        this.searchTerm = this.cleanValue(this.nodes.search.value);
        this.page = 1;
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.scheduleProductLoad();
      });

      document.addEventListener('click', this.handleProductCenterMenuClick);
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('resize', this.handleResize);
      this.nodes.filters?.addEventListener('scroll', this.handleFilterScroll, { passive: true });

      this.addEventListener('change', (event) => {
        const compareInput = event.target.closest('[data-product-center-compare-input]');
        if (compareInput) {
          this.toggleCompareProduct(compareInput);
          return;
        }

        const input = event.target.closest('[data-product-center-filter]');
        if (!input) return;
        const key = input.dataset.key;
        const value = input.value;
        if (!this.selectedFilters.has(key)) this.selectedFilters.set(key, new Set());
        const values = this.selectedFilters.get(key);
        if (input.checked) {
          values.add(value);
        } else {
          values.delete(value);
          if (values.size === 0) this.selectedFilters.delete(key);
        }
        if (this.isChipPlatformFilter(key)) {
          this.reconcileSystemOnChipSelection();
          this.renderFilters();
        }
        this.page = 1;
        this.syncProductCenterUrlState({ replace: true });
        this.applyFilters();
      });

      this.addEventListener('click', (event) => {
        const expand = event.target.closest('[data-product-center-expand]');
        if (expand) {
          const card = expand.closest('.product-center__card');
          const expanded = !card?.classList.contains('is-expanded');
          card?.classList.toggle('is-expanded', expanded);
          expand.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          this.syncFilterPanelHeight();
          return;
        }

        const toggle = event.target.closest('[data-product-center-filter-toggle]');
        if (toggle) {
          toggle.closest('.product-center__filter-group')?.classList.toggle('is-collapsed');
          this.syncFilterVisibilityButton();
          this.syncFilterPanelHeight();
          return;
        }

        const more = event.target.closest('[data-product-center-more]');
        if (more) {
          const group = more.closest('.product-center__filter-group');
          const expanded = group?.classList.toggle('is-expanded');
          more.textContent = expanded ? 'Less' : 'More';
          this.syncFilterPanelHeight();
          return;
        }

        const tab = event.target.closest('[data-product-center-category]');
        if (tab) {
          if (this.hasSearchTerm()) {
            this.searchTerm = '';
            this.clearSearchDebounce();
            if (this.nodes.search) this.nodes.search.value = '';
          }
          this.activeCategory = tab.dataset.value || '';
          this.page = 1;
          this.renderTabs();
          this.syncProductCenterUrlState();
          this.loadProducts();
          return;
        }

        const pageButton = event.target.closest('[data-product-center-page]');
        if (pageButton) {
          this.page = Number(pageButton.dataset.productCenterPage) || 1;
          this.renderProducts();
          this.syncProductCenterUrlState();
          return;
        }

        const removeCompare = event.target.closest('[data-product-center-compare-remove]');
        if (removeCompare) {
          this.removeCompareProduct(removeCompare.dataset.productCenterCompareRemove);
          return;
        }

        const clearCompare = event.target.closest('[data-product-center-compare-clear]');
        if (clearCompare) {
          this.clearCompareProducts();
          return;
        }

        const compareAction = event.target.closest('[data-product-center-compare-action]');
        if (compareAction) {
          this.goCompare();
        }
      });
    }

    disconnectedCallback() {
      this.clearSearchDebounce();
      document.removeEventListener('click', this.handleProductCenterMenuClick);
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('resize', this.handleResize);
      this.nodes.filters?.removeEventListener('scroll', this.handleFilterScroll);
      window.removeEventListener('pointermove', this.handleFilterScrollbarPointerMove);
      window.removeEventListener('pointerup', this.handleFilterScrollbarPointerUp);
      this.filterPanelResizeObserver?.disconnect();
    }

    clearSearchDebounce() {
      if (!this.searchDebounceTimer) return;
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    scheduleProductLoad() {
      this.clearSearchDebounce();
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchDebounceTimer = null;
        this.loadProducts();
      }, this.hasSearchTerm() ? 260 : 0);
    }

    onProductCenterMenuClick(event) {
      const link = event.target.closest('.products-mega-menu a[href]');
      if (!link) return;
      if (link.closest('footer, .section-footer, .firefly-footer-desktop, .firefly-footer-mobile')) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (_error) {
        return;
      }

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (!this.hasProductCenterParams(url)) return;

      event.preventDefault();
      this.applyProductCenterUrl(url);
    }

    applyProductCenterUrl(url, { updateHistory = true, shouldScroll = true } = {}) {
      const currentUrl = new URL(window.location.href);
      if (updateHistory && (url.search !== currentUrl.search || url.hash !== currentUrl.hash)) {
        window.history.pushState({ path: url.href }, '', url.href);
      }

      this.urlState = this.readUrlState(url.search);
      this.selectedFilters.clear();
      this.applyUrlFilters(this.urlState.filters);
      this.applyUrlState({ reset: true });
      if (this.nodes.search) this.nodes.search.value = this.searchTerm;

      this.clearSearchDebounce();
      this.renderTabs();
      this.renderFilters();
      this.loadProducts();
      if (shouldScroll) this.scrollToProductCenter();
    }

    isProductCenterUrl(url) {
      return (
        this.hasProductCenterParams(url) ||
        url.pathname.includes('/pages/product-center')
      );
    }

    hasProductCenterParams(url) {
      return (
        url.searchParams.has('pc_category') ||
        url.searchParams.has('pc_collection') ||
        url.searchParams.has('pc_page') ||
        url.searchParams.has('pc_filter') ||
        url.searchParams.has('pc_search') ||
        url.searchParams.has('pc_q')
      );
    }

    scrollToProductCenterFromUrlState() {
      if (!this.hasUrlState()) return;
      window.requestAnimationFrame(() => {
        window.setTimeout(() => this.scrollToProductCenter(), 60);
      });
    }

    hasUrlState() {
      return Boolean(
        this.cleanValue(this.urlState?.category) ||
        this.cleanValue(this.urlState?.search) ||
        this.urlState?.filters?.length ||
        Number(this.urlState?.page) > 1
      );
    }

    syncProductCenterUrlState({ replace = false } = {}) {
      let url;
      try {
        url = new URL(window.location.href);
      } catch (_error) {
        return;
      }

      const category = this.cleanValue(this.activeCategory);
      if (category) {
        url.searchParams.set('pc_category', category);
      } else {
        url.searchParams.delete('pc_category');
      }
      url.searchParams.delete('pc_collection');

      url.searchParams.delete('pc_filter');
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (/^pc_filter\[.+\]$/.test(key)) url.searchParams.delete(key);
      });
      this.selectedFilters.forEach((values, key) => {
        const ref = this.normalizeMetafieldRef(key);
        const filterKey = ref.namespace === 'custom' ? ref.shortKey : ref.canonical;
        if (!filterKey) return;
        values.forEach((value) => {
          const filterValue = this.cleanValue(value);
          if (filterValue) url.searchParams.append('pc_filter', `${filterKey}:${filterValue}`);
        });
      });

      url.searchParams.delete('pc_q');
      const searchTerm = this.cleanValue(this.searchTerm);
      if (searchTerm) {
        url.searchParams.set('pc_search', searchTerm);
      } else {
        url.searchParams.delete('pc_search');
      }

      const page = Math.max(1, Number.parseInt(this.page, 10) || 1);
      if (page > 1) {
        url.searchParams.set('pc_page', String(page));
      } else {
        url.searchParams.delete('pc_page');
      }

      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const next = `${url.pathname}${url.search}${url.hash}`;
      if (next === current) return;

      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ path: url.href }, '', url.href);
      this.urlState = this.readUrlState(url.search);
    }

    scrollToProductCenter() {
      const offset = window.innerWidth < 768 ? 76 : 96;
      const pageWrapper = document.querySelector('.page-wrapper');
      const pageWrapperStyle = pageWrapper ? window.getComputedStyle(pageWrapper) : null;
      const pageWrapperScrollable = pageWrapper &&
        (pageWrapperStyle.overflowY === 'auto' || pageWrapperStyle.overflowY === 'scroll') &&
        pageWrapper.scrollHeight > pageWrapper.clientHeight;

      if (pageWrapperScrollable) {
        const wrapperRect = pageWrapper.getBoundingClientRect();
        const top = Math.max(0, pageWrapper.scrollTop + this.getBoundingClientRect().top - wrapperRect.top - offset);
        pageWrapper.scrollTo({ top, behavior: 'auto' });
        return;
      }

      const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top, behavior: 'auto' });
    }

    syncFilterVisibilityButton() {
      if (!this.nodes.filterVisibility) return;
      const groups = Array.from(this.querySelectorAll('.product-center__filter-group'));
      const collapsed = groups.length > 0 && groups.every((group) => group.classList.contains('is-collapsed'));
      this.classList.toggle('is-filter-groups-collapsed', collapsed);
      this.nodes.filterVisibility.textContent = collapsed ? 'Show Filter' : 'Hide Filter';
      this.nodes.filterVisibility.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    readCompareItems() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem('pcmp_selected_products_v1') || '[]');
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed
          .map((item) => ({
            handle: this.cleanValue(item?.handle).toLowerCase(),
            title: this.cleanValue(item?.title),
            image: this.cleanValue(item?.image)
          }))
          .filter((item) => {
            if (!item.handle || seen.has(item.handle)) return false;
            seen.add(item.handle);
            return true;
          })
          .slice(0, 5);
      } catch (_error) {
        return [];
      }
    }

    writeCompareItems() {
      try {
        window.localStorage.setItem('pcmp_selected_products_v1', JSON.stringify(this.selectedCompareProducts));
      } catch (_error) {
        /* no-op */
      }
    }

    isCompareSelected(handle) {
      const normalized = this.cleanValue(handle).toLowerCase();
      return this.selectedCompareProducts.some((item) => item.handle === normalized);
    }

    toggleCompareProduct(input) {
      const item = {
        handle: this.cleanValue(input.dataset.productHandle).toLowerCase(),
        title: this.cleanValue(input.dataset.productTitle),
        image: this.cleanValue(input.dataset.productImage)
      };

      if (!item.handle) return;

      if (input.checked) {
        if (!this.isCompareSelected(item.handle)) {
          if (this.selectedCompareProducts.length >= 5) {
            input.checked = false;
            window.alert('You can compare up to 5 products.');
            return;
          }
          this.selectedCompareProducts.push(item);
        }
      } else {
        this.selectedCompareProducts = this.selectedCompareProducts.filter((product) => product.handle !== item.handle);
      }

      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    removeCompareProduct(handle) {
      const normalized = this.cleanValue(handle).toLowerCase();
      this.selectedCompareProducts = this.selectedCompareProducts.filter((product) => product.handle !== normalized);
      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    clearCompareProducts() {
      this.selectedCompareProducts = [];
      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    syncCompareInputs() {
      this.querySelectorAll('[data-product-center-compare-input]').forEach((input) => {
        input.checked = this.isCompareSelected(input.dataset.productHandle);
      });
    }

    renderCompareBar() {
      if (!this.nodes.compareBar) return;
      const items = this.selectedCompareProducts;
      this.nodes.compareBar.hidden = items.length === 0;
      this.classList.toggle('has-compare-bar', items.length > 0);
      if (items.length === 0) {
        this.nodes.compareBar.innerHTML = '';
        return;
      }

      const cards = items.map((item, index) => `
        <article class="product-center__compare-item${index === items.length - 1 && items.length === 5 ? ' is-last' : ''}">
          <div class="product-center__compare-thumb">
            ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="product-center__compare-meta">
            <p title="${this.escapeHtml(item.title || item.handle)}">${this.escapeHtml(item.title || item.handle)}</p>
            <button type="button" data-product-center-compare-remove="${this.escapeHtml(item.handle)}">Remove</button>
          </div>
        </article>
      `).join('');

      this.nodes.compareBar.innerHTML = `
        <div class="product-center__compare-panel">
          <div class="product-center__compare-head">
            <span>Product</span>
            <div class="product-center__compare-actions">
              <button class="product-center__compare-clear" type="button" data-product-center-compare-clear>Clear</button>
              <button class="product-center__compare-submit" type="button" data-product-center-compare-action ${items.length < 2 ? 'disabled' : ''}>Compare</button>
            </div>
          </div>
          <div class="product-center__compare-list">
            ${cards}
            ${items.length < 5 ? '<p class="product-center__compare-add">You can continue to add</p>' : ''}
          </div>
        </div>
      `;
    }

    goCompare() {
      if (this.selectedCompareProducts.length < 2) {
        window.alert('Select at least 2 products to compare.');
        return;
      }
      const url = new URL('/pages/product-comparison', window.location.origin);
      url.searchParams.set('view', 'product-comparison');
      url.searchParams.set('ids', this.selectedCompareProducts.map((item) => item.handle).join(','));
      window.open(`${url.pathname}${url.search}`, '_blank', 'noopener,noreferrer');
    }

    renderFilters() {
      if (!this.nodes.filters) return;
      this.nodes.filters.innerHTML = this.filterGroups.map((group) => {
        const visibleOptions = this.visibleFilterOptions(group);
        const hasSelectedExtra = visibleOptions.some((option, index) => index > 4 && this.isFilterSelected(group.key, option.value));
        const options = visibleOptions.map((option, index) => `
          <label class="product-center__checkbox${index > 4 ? ' is-extra' : ''}">
            <input type="checkbox" value="${this.escapeHtml(option.value)}" data-key="${this.escapeHtml(group.key)}" data-product-center-filter>
            <span>${this.escapeHtml(option.label)}</span>
          </label>
        `).join('');

        return `
          <section class="product-center__filter-group${hasSelectedExtra ? ' is-expanded' : ''}">
            <button class="product-center__filter-toggle" type="button" data-product-center-filter-toggle>
              <span>${this.escapeHtml(group.label)}</span>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="product-center__filter-options">
              ${options}
              ${visibleOptions.length > 5 ? `<button class="product-center__more" type="button" data-product-center-more>${hasSelectedExtra ? 'Less' : 'More'}</button>` : ''}
            </div>
          </section>
        `;
      }).join('');
      this.syncFilterInputs();
      this.syncFilterPanelHeight();
    }

    visibleFilterOptions(group) {
      const options = Array.isArray(group?.options) ? group.options : [];
      if (!this.isSystemOnChipFilter(group?.key)) return options;

      const selectedPlatformKeys = this.selectedChipPlatformIdentityKeys();
      if (selectedPlatformKeys.size === 0) return options;

      return options.filter((option) => {
        const belongingPlatformKeys = Array.isArray(option?.belongingPlatformKeys)
          ? option.belongingPlatformKeys
          : [];
        return belongingPlatformKeys.some((key) => selectedPlatformKeys.has(key));
      });
    }

    selectedChipPlatformIdentityKeys() {
      const platformConfig = this.metaobjectFilterConfigByType('chip_platform');
      if (!platformConfig) return new Set();

      const platformGroup = this.filterGroups.find((group) => group.key === platformConfig.key);
      if (!platformGroup) return new Set();

      const selectedOptions = platformGroup.options.filter((option) => (
        this.isFilterSelected(platformGroup.key, option.value)
      ));

      return new Set(selectedOptions.flatMap((option) => option.identityKeys || []));
    }

    reconcileSystemOnChipSelection() {
      const systemOnChipConfig = this.metaobjectFilterConfigByType('system_on_chip');
      if (!systemOnChipConfig) return;

      const selectedValues = this.selectedFilters.get(systemOnChipConfig.key);
      if (!selectedValues?.size) return;

      const systemOnChipGroup = this.filterGroups.find((group) => group.key === systemOnChipConfig.key);
      if (!systemOnChipGroup) return;

      const visibleValues = new Set(
        this.visibleFilterOptions(systemOnChipGroup).map((option) => this.normalizeUrlToken(option.value))
      );

      Array.from(selectedValues).forEach((value) => {
        if (!visibleValues.has(this.normalizeUrlToken(value))) selectedValues.delete(value);
      });
      if (selectedValues.size === 0) this.selectedFilters.delete(systemOnChipConfig.key);
    }

    metaobjectFilterConfigByType(type) {
      const target = this.normalizeUrlToken(type);
      return this.metaobjectFilterGroupConfigs.find((group) => this.normalizeUrlToken(group.type) === target) || null;
    }

    isChipPlatformFilter(key) {
      return this.metaobjectFilterConfigByType('chip_platform')?.key === this.normalizeMetafieldRef(key).canonical;
    }

    isSystemOnChipFilter(key) {
      return this.metaobjectFilterConfigByType('system_on_chip')?.key === this.normalizeMetafieldRef(key).canonical;
    }

    syncFilterInputs() {
      this.querySelectorAll('[data-product-center-filter]').forEach((input) => {
        input.checked = this.isFilterSelected(input.dataset.key, input.value);
      });
    }

    isFilterSelected(key, value) {
      const ref = this.normalizeMetafieldRef(key);
      const selectedValues = this.selectedFilters.get(ref.canonical);
      if (!selectedValues?.size) return false;
      const target = this.normalizeUrlToken(value);
      return Array.from(selectedValues).some((selected) => this.normalizeUrlToken(selected) === target);
    }

    renderTabs() {
      if (!this.nodes.tabs) return;
      const searching = this.hasSearchTerm();
      this.nodes.tabs.classList.toggle('is-searching', searching);
      this.nodes.tabs.innerHTML = this.categories.map((category, index) => {
        const value = category.value || category.collectionHandle || category.label;
        const active = !searching && (value === this.activeCategory || (!this.activeCategory && index === 0));
        return `<button class="product-center__tab${active ? ' is-active' : ''}" type="button" data-product-center-category data-value="${this.escapeHtml(value)}">${this.escapeHtml(category.label)}</button>`;
      }).join('');
      this.scrollActiveTabIntoView();
    }

    scrollActiveTabIntoView() {
      const activeTab = this.nodes.tabs?.querySelector('.product-center__tab.is-active');
      if (!activeTab) return;
      window.requestAnimationFrame(() => {
        activeTab.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'nearest'
        });
      });
    }

    async loadProducts() {
      if (!this.storeDomain || !this.storefrontToken) {
        this.setStatus('Configure Storefront API settings to load products.');
        this.products = [];
        this.applyFilters();
        return;
      }

      const requestId = this.requestId + 1;
      this.requestId = requestId;
      this.isLoadingProducts = true;
      this.products = [];
      this.filteredProducts = [];
      this.renderProducts();
      this.setStatus(this.config.loadingText || 'Loading products...');

      try {
        this.products = this.hasSearchTerm()
          ? await this.fetchSearchProducts(this.searchTerm)
          : await this.fetchAllProducts(this.getActiveCollectionHandle());
        if (requestId !== this.requestId) return;
        this.isLoadingProducts = false;
        this.applyFilters();
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.isLoadingProducts = false;
        this.products = [];
        this.applyFilters();
        this.setStatus(`Failed to load products: ${this.cleanValue(error?.message) || 'Unknown error'}`);
      }
    }

    async loadMetaobjectFilters() {
      if (!this.storeDomain || !this.storefrontToken || this.metaobjectFilterGroupConfigs.length === 0) {
        return;
      }

      try {
        const results = await Promise.allSettled(this.metaobjectFilterGroupConfigs.map((group) => this.fetchMetaobjectFilterGroup(group)));
        const groups = results
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value);
        const failed = results.filter((result) => result.status === 'rejected');
        if (failed.length) {
          console.warn('Some product center metaobject filters failed to load:', failed.map((result) => result.reason));
        }
        this.filterGroups = [
          ...groups.filter((group) => group.options.length > 0),
          ...this.manualFilterGroups
        ];
        this.reconcileSystemOnChipSelection();
        this.renderFilters();
      } catch (error) {
        console.warn('Failed to load product center metaobject filters:', error);
      }
    }

    async fetchMetaobjectFilterGroup(group) {
      const options = [];
      const seen = new Set();
      let hasNextPage = true;
      let after = null;

      while (hasNextPage) {
        const payload = await this.storefrontRequest(this.metaobjectsQuery(), {
          type: group.type,
          first: 250,
          after
        });
        const connection = payload?.data?.metaobjects;
        const edges = connection?.edges || [];

        edges.forEach((edge) => {
          const option = this.metaobjectOptionFromNode(edge?.node, group);
          if (!option) return;
          const key = option.key || this.normalize(option.value);
          if (!key || seen.has(key)) return;
          seen.add(key);
          options.push(option);
        });

        hasNextPage = Boolean(connection?.pageInfo?.hasNextPage);
        after = connection?.pageInfo?.endCursor || null;
      }

      options.sort((a, b) => {
        if (a.sort !== b.sort) return a.sort - b.sort;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });

      return {
        label: group.label,
        key: group.key,
        type: group.type,
        options
      };
    }

    async fetchAllProducts(collectionHandle) {
      const products = [];
      const pageSize = Number(this.config.fetchPageSize) || 100;
      const maxProducts = Number(this.config.maxProducts) || 0;
      let after = null;
      let hasNextPage = true;
      let useRootProducts = !collectionHandle || this.normalize(collectionHandle) === 'all';

      while (hasNextPage) {
        const variables = {
          first: Math.min(pageSize, 250),
          after
        };
        if (!useRootProducts) variables.handle = collectionHandle;

        const payload = await this.storefrontRequest(useRootProducts ? this.productsQuery() : this.collectionProductsQuery(), variables);

        if (!useRootProducts && !payload?.data?.collection) {
          useRootProducts = true;
          after = null;
          products.length = 0;
          continue;
        }

        const productConnection = useRootProducts ? payload?.data?.products : payload?.data?.collection?.products;
        const edges = productConnection?.edges || [];
        edges.forEach((edge) => {
          const product = this.productFromNode(edge?.node);
          if (product) products.push(product);
        });

        hasNextPage = Boolean(productConnection?.pageInfo?.hasNextPage);
        after = productConnection?.pageInfo?.endCursor || null;

        if (maxProducts > 0 && products.length >= maxProducts) {
          return products.slice(0, maxProducts);
        }
      }

      return products;
    }

    async fetchSearchProducts(term) {
      const products = [];
      const pageSize = Number(this.config.fetchPageSize) || 100;
      const maxProducts = Number(this.config.maxProducts) || 0;
      const query = this.cleanValue(term);
      let after = null;
      let hasNextPage = Boolean(query);

      while (hasNextPage) {
        const payload = await this.storefrontRequest(this.productSearchQuery(), {
          first: Math.min(pageSize, 250),
          after,
          query
        });
        const productConnection = payload?.data?.products;
        const edges = productConnection?.edges || [];
        edges.forEach((edge) => {
          const product = this.productFromNode(edge?.node);
          if (product) products.push(product);
        });

        hasNextPage = Boolean(productConnection?.pageInfo?.hasNextPage);
        after = productConnection?.pageInfo?.endCursor || null;

        if (maxProducts > 0 && products.length >= maxProducts) {
          return products.slice(0, maxProducts);
        }
      }

      return products;
    }

    applyFilters() {
      this.filteredProducts = this.products.filter((product) => {
        if (this.hasSearchTerm()) {
          if (!this.matchesTitleSearch(product)) return false;
        } else if (!this.matchesCategory(product)) {
          return false;
        }
        return this.matchesSelectedFilters(product);
      });
      this.updateResetCount();
      this.renderProducts();
    }

    matchesCategory(product) {
      if (this.getActiveCategory()?.collectionHandle) return true;
      if (!this.activeCategory) return true;
      const target = this.normalize(this.activeCategory);
      const values = [
        product.productType,
        product.resourceCategory,
        product.category,
        ...(product.tags || [])
      ].map((item) => this.normalize(item)).filter(Boolean);
      return values.some((value) => value === target || value.includes(target) || target.includes(value));
    }

    getActiveCategory() {
      return this.categories.find((category) => {
        const value = category.value || category.collectionHandle || category.label;
        return value === this.activeCategory;
      }) || null;
    }

    getActiveCollectionHandle() {
      return this.cleanValue(this.getActiveCategory()?.collectionHandle) || this.fallbackCollectionHandle;
    }

    matchesSearch(product) {
      const term = this.normalize(this.searchTerm);
      if (!term) return true;
      const haystack = [
        product.title,
        product.productType,
        product.vendor,
        product.resourceCategory,
        product.chip_platform,
        product.system_on_chip,
        product.soc,
        product.chip_type,
        ...this.metafieldRefs.map((ref) => product[ref.canonical]),
        ...(product.tags || [])
      ].join(' ');
      return this.normalize(haystack).includes(term);
    }

    matchesTitleSearch(product) {
      const term = this.normalize(this.searchTerm);
      if (!term) return true;
      return this.normalize(product?.title).includes(term);
    }

    hasSearchTerm() {
      return Boolean(this.cleanValue(this.searchTerm));
    }

    matchesSelectedFilters(product) {
      for (const [key, selectedValues] of this.selectedFilters.entries()) {
        const productValue = this.normalize(this.getProductValue(product, key));
        if (!productValue) return false;
        const hasMatch = Array.from(selectedValues).some((selected) => {
          const normalized = this.normalize(selected);
          return productValue === normalized || productValue.includes(normalized) || normalized.includes(productValue);
        });
        if (!hasMatch) return false;
      }
      return true;
    }

    renderProducts() {
      const total = this.filteredProducts.length;
      const pageSize = Number(this.config.pageSize) || 9;

      if (this.nodes.summary) {
        this.nodes.summary.textContent = this.hasSearchTerm()
          ? `Search results for “${this.searchTerm}”：${total} products`
          : `Total：${total} products`;
      }

      if (!this.products.length) {
        if (!this.isLoadingProducts && this.page !== 1) {
          this.page = 1;
          this.syncProductCenterUrlState({ replace: true });
        }
        this.nodes.grid.innerHTML = '';
        this.renderPagination(0);
        this.syncFilterPanelHeight();
        if (!this.nodes.status.textContent) this.setStatus(this.config.emptyText || 'No products match the selected filters.');
        return;
      }

      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      const requestedPage = this.page;
      this.page = Math.min(Math.max(1, this.page), maxPage);
      if (this.page !== requestedPage) this.syncProductCenterUrlState({ replace: true });
      const start = (this.page - 1) * pageSize;
      const visible = this.filteredProducts.slice(start, start + pageSize);

      this.setStatus(total ? '' : (this.config.emptyText || 'No products match the selected filters.'));
      this.nodes.grid.innerHTML = visible.map((product) => this.renderProductCard(product)).join('');
      this.syncCompareInputs();
      this.renderPagination(maxPage);
      this.syncFilterPanelHeight();
    }

    renderProductCard(product) {
      const image = product.image?.url
        ? `<img src="${this.escapeHtml(product.image.url)}" alt="${this.escapeHtml(product.image.altText || product.title)}" loading="lazy">`
        : '<div class="product-center__product-placeholder" aria-hidden="true"></div>';
      const href = product.onlineStoreUrl || `/products/${product.handle}`;
      const title = this.getProductValue(product, this.cardTitleField) || product.title;
      const ribbon = this.getProductValue(product, this.cardRibbonField) || 'No Data';
      const specs = this.cardSpecs.map((spec) => [spec.label, this.getFirstProductValue(product, spec.keys)]);
      const imageUrl = product.image?.url || '';
      const checked = this.isCompareSelected(product.handle) ? ' checked' : '';

      return `
        <article class="product-center__card" data-product-title="${this.escapeHtml(product.title)}">
          <input
            class="product-center__compare"
            type="checkbox"
            aria-label="Select ${this.escapeHtml(product.title)}"
            data-product-center-compare-input
            data-product-handle="${this.escapeHtml(product.handle)}"
            data-product-title="${this.escapeHtml(title)}"
            data-product-image="${this.escapeHtml(imageUrl)}"${checked}>
          <button class="product-center__expand" type="button" aria-label="Expand ${this.escapeHtml(product.title)}" aria-expanded="false" data-product-center-expand></button>
          <div class="product-center__ribbon">${this.escapeHtml(ribbon)}</div>
          <div class="product-center__expanded-ribbon">${this.escapeHtml(ribbon)}</div>
          <a class="product-center__product-media" href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${image}</a>
          <h2 class="product-center__product-title" title="${this.escapeHtml(title)}">${this.escapeHtml(title)}</h2>
          <div class="product-center__expanded-title" title="${this.escapeHtml(title)}">${this.escapeHtml(title)}</div>
          <div class="product-center__specs">
            ${specs.map(([label, value]) => `
              <p class="product-center__spec">
                <span class="product-center__spec-label">${this.escapeHtml(label)}</span>
                <span class="product-center__spec-value" title="${this.escapeHtml(value || '-')}">${this.escapeHtml(value || '-')}</span>
              </p>
            `).join('')}
          </div>
          <a class="product-center__buy" href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(this.config.buyLabel || 'Buy Now')}</a>
        </article>
      `;
    }

    renderPagination(maxPage) {
      if (!this.nodes.pagination) return;
      if (maxPage <= 1) {
        this.nodes.pagination.innerHTML = '';
        return;
      }

      const visibleCount = Math.min(maxPage, 5);
      const startPage = Math.min(Math.max(1, this.page - Math.floor(visibleCount / 2)), maxPage - visibleCount + 1);
      const endPage = startPage + visibleCount - 1;
      const pages = [
        this.renderPaginationButton(this.page - 1, 'Previous page', 'prev', this.page <= 1)
      ];

      for (let index = startPage; index <= endPage; index += 1) {
        pages.push(this.renderPaginationButton(index, `Page ${index}`, index, false, index === this.page));
      }

      pages.push(this.renderPaginationButton(this.page + 1, 'Next page', 'next', this.page >= maxPage));
      this.nodes.pagination.innerHTML = pages.join('');
    }

    renderPaginationButton(page, label, content, disabled = false, active = false) {
      const contentMarkup = content === 'prev' || content === 'next'
        ? '<svg viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M5.5 1L1.5 6L5.5 11" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : this.escapeHtml(content);
      return `
        <button
          class="product-center__page${active ? ' is-active' : ''}${content === 'next' ? ' is-next' : ''}"
          type="button"
          data-product-center-page="${disabled ? this.page : page}"
          aria-label="${this.escapeHtml(label)}"
          ${active ? 'aria-current="page"' : ''}
          ${disabled ? 'disabled' : ''}>
          ${contentMarkup}
        </button>
      `;
    }

    syncFilterPanelHeight() {
      if (!this.nodes.filterPanel || !this.nodes.grid || window.innerWidth < 768) {
        this.style.removeProperty('--product-center-filter-height');
        this.style.removeProperty('--product-center-filter-groups-height');
        return;
      }

      window.requestAnimationFrame(() => {
        const filterRect = this.nodes.filterPanel.getBoundingClientRect();
        const gridRect = this.nodes.grid.getBoundingClientRect();
        const groupsRect = this.nodes.filters?.getBoundingClientRect();
        const minFilterHeight = 1231;
        const height = Math.max(minFilterHeight, gridRect.bottom - filterRect.top);
        const groupsHeight = groupsRect ? Math.max(0, height - (groupsRect.top - filterRect.top)) : 0;
        this.style.setProperty('--product-center-filter-height', `${Math.ceil(height)}px`);
        if (groupsHeight) {
          this.style.setProperty('--product-center-filter-groups-height', `${Math.ceil(groupsHeight)}px`);
        }
        window.requestAnimationFrame(() => this.updateFilterScrollbar());
      });
    }

    observeFilterPanelHeight() {
      if (!this.nodes.grid || typeof ResizeObserver !== 'function') return;
      this.filterPanelResizeObserver?.disconnect();
      this.filterPanelResizeObserver = new ResizeObserver(() => this.syncFilterPanelHeight());
      this.filterPanelResizeObserver.observe(this.nodes.grid);
    }

    ensureFilterScrollbar() {
      if (!this.nodes.filterPanel || this.nodes.filterScrollbar) return;
      const scrollbar = document.createElement('div');
      scrollbar.className = 'product-center__filter-scrollbar';
      scrollbar.hidden = true;
      scrollbar.innerHTML = '<span class="product-center__filter-scrollbar-thumb" aria-hidden="true"></span>';
      this.nodes.filterPanel.append(scrollbar);
      this.nodes.filterScrollbar = scrollbar;
      this.nodes.filterScrollbarThumb = scrollbar.querySelector('.product-center__filter-scrollbar-thumb');
      scrollbar.addEventListener('pointerdown', (event) => this.onFilterScrollbarPointerDown(event));
    }

    updateFilterScrollbar() {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb || window.innerWidth < 768) {
        if (scrollbar) scrollbar.hidden = true;
        return;
      }

      const scrollHeight = scroller.scrollHeight;
      const clientHeight = scroller.clientHeight;
      if (scrollHeight <= clientHeight + 1) {
        scrollbar.hidden = true;
        return;
      }

      scrollbar.hidden = false;
      const trackHeight = scrollbar.clientHeight;
      const thumbHeight = Math.max(28, Math.round((clientHeight / scrollHeight) * trackHeight));
      const maxScroll = scrollHeight - clientHeight;
      const maxY = Math.max(0, trackHeight - thumbHeight);
      const thumbY = maxScroll > 0 ? Math.round((scroller.scrollTop / maxScroll) * maxY) : 0;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.setProperty('--product-center-filter-thumb-y', `${thumbY}px`);
    }

    onFilterScrollbarPointerDown(event) {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb || scrollbar.hidden) return;

      event.preventDefault();
      const trackRect = scrollbar.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const isThumb = event.target === thumb;

      if (!isThumb) {
        this.scrollFilterGroupsToTrackPosition(event.clientY - trackRect.top);
      }

      this.filterScrollbarDrag = {
        startY: event.clientY,
        startScrollTop: scroller.scrollTop,
        trackHeight: scrollbar.clientHeight,
        thumbHeight: thumb.offsetHeight
      };

      window.addEventListener('pointermove', this.handleFilterScrollbarPointerMove);
      window.addEventListener('pointerup', this.handleFilterScrollbarPointerUp, { once: true });

      if (isThumb && thumb.setPointerCapture) {
        try {
          thumb.setPointerCapture(event.pointerId);
        } catch (_error) {
          /* no-op */
        }
      }
    }

    onFilterScrollbarPointerMove(event) {
      const scroller = this.nodes.filters;
      if (!scroller || !this.filterScrollbarDrag) return;

      event.preventDefault();
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      const maxY = Math.max(1, this.filterScrollbarDrag.trackHeight - this.filterScrollbarDrag.thumbHeight);
      const deltaY = event.clientY - this.filterScrollbarDrag.startY;
      scroller.scrollTop = this.filterScrollbarDrag.startScrollTop + (deltaY / maxY) * maxScroll;
    }

    onFilterScrollbarPointerUp() {
      this.filterScrollbarDrag = null;
      window.removeEventListener('pointermove', this.handleFilterScrollbarPointerMove);
    }

    scrollFilterGroupsToTrackPosition(trackY) {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb) return;

      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      const maxY = Math.max(1, scrollbar.clientHeight - thumb.offsetHeight);
      const targetY = Math.max(0, Math.min(maxY, trackY - thumb.offsetHeight / 2));
      scroller.scrollTop = (targetY / maxY) * maxScroll;
    }

    updateResetCount() {
      if (!this.nodes.reset) return;
      let count = this.searchTerm ? 1 : 0;
      this.selectedFilters.forEach((values) => { count += values.size; });
      this.nodes.reset.textContent = `Reset（${count}）`;
    }

    productFromNode(node) {
      if (!node) return null;
      const aggregateProductId = this.cleanValue(node.aggregateProduct?.value);
      if (aggregateProductId) return null;

      const product = {
        id: node.id,
        title: this.cleanValue(node.title),
        handle: this.cleanValue(node.handle),
        vendor: this.cleanValue(node.vendor),
        productType: this.cleanValue(node.productType),
        tags: Array.isArray(node.tags) ? node.tags : [],
        onlineStoreUrl: this.cleanValue(node.onlineStoreUrl),
        image: node.featuredImage || null
      };

      this.metafieldRefs.forEach((ref) => {
        const value = this.metafieldValue(node[ref.alias]);
        product[ref.canonical] = value;
        if (!product[ref.key]) product[ref.key] = value;
      });

      this.cardSpecs.forEach((spec) => {
        spec.fields.forEach((field) => {
          if (!field.displayField) return;
          const ref = this.metafieldRefs.find((item) => item.canonical === field.canonical);
          if (!ref) return;
          product[this.cardSpecFieldKey(field)] = this.metafieldValue(node[ref.alias], field.displayField);
        });
      });

      product.resourceCategory = product['custom.resource_category'] || product.resource_category || '';
      product.category = product['custom.category'] || product.category || '';

      return product;
    }

    getProductValue(product, key) {
      if (!product || !key) return '';
      const ref = this.normalizeMetafieldRef(key);
      if (product[ref.canonical] != null) return product[ref.canonical];
      if (product[key] != null) return product[key];
      if (product[ref.key] != null) return product[ref.key];
      return '';
    }

    getFirstProductValue(product, keys) {
      const keyList = Array.isArray(keys) ? keys : [keys];
      for (const key of keyList) {
        const value = typeof key === 'object'
          ? product[this.cardSpecFieldKey(key)]
          : this.getProductValue(product, key);
        if (this.cleanValue(value)) return value;
      }
      return '';
    }

    metafieldValue(field, displayField = '') {
      const values = [];
      const raw = this.formatMetafieldRawValue(field);
      if (raw) values.push(raw);

      const referenceValue = this.metaobjectText(field?.reference, displayField);
      if (referenceValue) values.push(referenceValue);

      const referenceNodes = field?.references?.nodes || [];
      referenceNodes.forEach((node) => {
        const value = this.metaobjectText(node, displayField);
        if (value) values.push(value);
      });

      return [...new Set(values)].join(' ');
    }

    formatMetafieldRawValue(field) {
      const raw = this.cleanValue(field?.value);
      if (!raw) return '';

      if (/^gid:\/\/shopify\//.test(raw)) return '';

      if (!/^[\[{]/.test(raw)) return raw;

      try {
        const parsed = JSON.parse(raw);
        if (field?.type === 'rich_text_field' || this.isRichTextNode(parsed)) {
          return this.richTextToText(parsed);
        }

        const flattened = this.stringifyMetafieldJson(parsed);
        if (/^gid:\/\/shopify\//.test(flattened)) return '';
        return flattened;
      } catch (_error) {
        return raw;
      }
    }

    parseFilterGroups(raw) {
      return String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, key, optionsRaw] = line.split('|');
          const ref = this.normalizeMetafieldRef(key);
          const options = String(optionsRaw || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => ({ label: item, value: item }));
          return {
            label: this.cleanValue(label),
            key: ref.canonical,
            options
          };
        })
        .filter((group) => group.label && group.key && group.options.length);
    }

    parseMetaobjectFilterGroups(raw) {
      return String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, type, key, displayField, valueField] = line.split('|').map((item) => this.cleanValue(item));
          return {
            label,
            type,
            key: this.normalizeMetafieldRef(key).canonical,
            displayField,
            valueField
          };
        })
        .filter((group) => group.label && group.type && group.key);
    }

    parseCardSpecs(raw) {
      const specs = String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, keysRaw, displayFieldRaw] = line.split('|');
          const displayField = this.cleanValue(displayFieldRaw);
          const fields = String(keysRaw || '')
            .split(',')
            .map((item) => {
              const ref = this.normalizeMetafieldRef(item);
              return {
                canonical: ref.canonical,
                displayField: displayField || this.defaultCardSpecDisplayField(ref)
              };
            })
            .filter((field) => field.canonical);
          const cleanLabel = this.cleanValue(label);
          return {
            label: cleanLabel ? `${cleanLabel}:` : '',
            keys: fields.map((field) => field.canonical),
            fields
          };
        })
        .filter((spec) => spec.label && spec.fields.length);

      return specs.length ? specs : [
        { label: 'SoC:', keys: ['custom.system_on_chip', 'custom.soc'], fields: [{ canonical: 'custom.system_on_chip', displayField: '' }, { canonical: 'custom.soc', displayField: '' }] },
        { label: 'Connector:', keys: ['custom.connector_type', 'custom.connector'], fields: [{ canonical: 'custom.connector_type', displayField: '' }, { canonical: 'custom.connector', displayField: '' }] },
        { label: 'Dimension:', keys: ['custom.dimension'], fields: [{ canonical: 'custom.dimension', displayField: '' }] },
        { label: 'Level:', keys: ['custom.environment', 'custom.operating_temperature'], fields: [{ canonical: 'custom.environment', displayField: '' }, { canonical: 'custom.operating_temperature', displayField: '' }] }
      ];
    }

    defaultCardSpecDisplayField(ref) {
      if (ref.canonical === 'custom1.connector') return 'name';
      return '';
    }

    collectMetafieldRefs() {
      const refs = [
        'custom.resource_category',
        'custom.subcategory',
        'custom.original_name',
        'custom.category',
        'custom.chip_platform',
        'custom.chip_type',
        'custom.system_on_chip',
        'custom.soc',
        'custom.npu',
        'custom.cpu',
        'custom.environment',
        'custom.operating_temperature',
        'custom.connector_type',
        'custom.connector',
        'custom.video_encoding',
        'custom.video_decoding',
        'custom.video_input',
        'custom.video_output',
        'custom.dimension'
      ];

      this.metaobjectFilterGroupConfigs.forEach((group) => refs.push(group.key));
      this.manualFilterGroups.forEach((group) => refs.push(group.key));
      this.selectedFilters.forEach((_values, key) => refs.push(key));
      if (this.cardRibbonField) refs.push(this.cardRibbonField);
      if (this.cardTitleField) refs.push(this.cardTitleField);
      this.cardSpecs.forEach((spec) => {
        spec.fields.forEach((field) => refs.push(field.canonical));
      });

      const seen = new Set();
      return refs
        .map((ref) => this.normalizeMetafieldRef(ref))
        .filter((ref) => ref.namespace && ref.key)
        .filter((ref) => {
          if (seen.has(ref.canonical)) return false;
          seen.add(ref.canonical);
          return true;
        })
        .map((ref, index) => ({
          ...ref,
          alias: `mf_${index}`
        }));
    }

    normalizeMetafieldRef(value) {
      const raw = this.cleanValue(value);
      if (!raw) return { namespace: 'custom', key: '', canonical: '', shortKey: '' };
      const dotIndex = raw.indexOf('.');
      const namespace = dotIndex > 0 ? raw.slice(0, dotIndex).trim() : 'custom';
      const key = dotIndex > 0 ? raw.slice(dotIndex + 1).trim() : raw;
      return {
        namespace,
        key,
        canonical: namespace && key ? `${namespace}.${key}` : '',
        shortKey: key
      };
    }

    cardSpecFieldKey(field) {
      const displayField = this.cleanValue(field?.displayField);
      return displayField ? `${field.canonical}::${displayField}` : field.canonical;
    }

    metaobjectOptionFromNode(node, group) {
      if (!node) return null;
      const fieldMap = this.metaobjectFieldMap(node);
      const label = this.metaobjectDisplayText(node, group);
      if (!label) return null;
      const value = this.metaobjectMatchText(node, group, label);
      const identityKeys = this.metaobjectIdentityKeys(node, label, value);
      const belongingPlatformKeys = this.normalizeReferenceKeys(fieldMap.get('belonging_platform'));

      const sortRaw = fieldMap.get('sort_order') || fieldMap.get('sort') || fieldMap.get('order') || fieldMap.get('position');
      const sortValue = Number(sortRaw);

      return {
        key: this.cleanValue(node.id) || this.normalize(label),
        label,
        value,
        identityKeys,
        belongingPlatformKeys,
        sort: Number.isFinite(sortValue) ? sortValue : 999999
      };
    }

    metaobjectIdentityKeys(node, label = '', value = '') {
      return [...new Set([
        node?.id,
        node?.handle,
        label,
        value
      ].map((item) => this.normalizeReferenceKey(item)).filter(Boolean))];
    }

    normalizeReferenceKeys(rawValue) {
      const raw = this.cleanValue(rawValue);
      if (!raw) return [];

      let values = [raw];
      if (/^[\[\"]/.test(raw)) {
        try {
          const parsed = JSON.parse(raw);
          values = Array.isArray(parsed) ? parsed : [parsed];
        } catch (_error) {
          values = [raw];
        }
      }

      return [...new Set(values.map((item) => this.normalizeReferenceKey(item)).filter(Boolean))];
    }

    normalizeReferenceKey(value) {
      return this.cleanValue(value).toLowerCase();
    }

    metaobjectText(node, displayField = '') {
      if (!node) return '';
      const fieldMap = this.metaobjectFieldMap(node);
      if (displayField) return this.cleanValue(fieldMap.get(displayField));
      return this.metaobjectDisplayText(node) || this.metaobjectFieldValuesText(node);
    }

    metaobjectFieldValuesText(node) {
      const fields = Array.isArray(node?.fields) ? node.fields : [];
      const values = fields
        .filter((field) => !/^(sort|sort_order|order|position)$/i.test(this.cleanValue(field?.key)))
        .map((field) => this.cleanValue(field?.value))
        .filter((value) => value && !/^gid:\/\/shopify\//.test(value));

      return [...new Set(values)].join(' ');
    }

    metaobjectDisplayText(node, group = {}) {
      const fieldMap = this.metaobjectFieldMap(node);
      return this.cleanValue(
        (group.displayField && fieldMap.get(group.displayField)) ||
        this.firstMetaobjectField(fieldMap, ['connector_name', 'connector_value', 'parameter_name', 'parameter_value', 'param_name', 'param_value', 'display_name', 'name', 'title', 'label', 'value'])
      );
    }

    metaobjectMatchText(node, group = {}, fallback = '') {
      const fieldMap = this.metaobjectFieldMap(node);
      if (group.valueField) return this.cleanValue(fieldMap.get(group.valueField));
      const values = [
        fallback,
        this.firstMetaobjectField(fieldMap, ['connector_name', 'connector_value', 'parameter_name', 'parameter_value', 'param_name', 'param_value', 'display_name', 'name', 'title', 'label', 'value']),
        node.handle
      ];
      return [...new Set(values.map((item) => this.cleanValue(item)).filter(Boolean))].join(' ');
    }

    firstMetaobjectField(fieldMap, keys) {
      for (const key of keys) {
        const value = this.cleanValue(fieldMap.get(key));
        if (value) return value;
      }
      return '';
    }

    metaobjectFieldMap(node) {
      const fields = Array.isArray(node?.fields) ? node.fields : [];
      return new Map(fields.map((field) => [this.cleanValue(field?.key), this.cleanValue(field?.value)]));
    }

    isRichTextNode(value) {
      return Boolean(value && typeof value === 'object' && typeof value.type === 'string' && Array.isArray(value.children));
    }

    richTextToText(node) {
      if (!node) return '';

      if (Array.isArray(node)) {
        return node.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
      }

      if (typeof node !== 'object') {
        return this.cleanValue(node);
      }

      if (node.type === 'text') {
        return this.cleanValue(node.value);
      }

      const children = Array.isArray(node.children) ? node.children : [];

      if (node.type === 'list') {
        return children.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
      }

      if (node.type === 'list-item' || node.type === 'paragraph') {
        return children.map((item) => this.richTextToText(item)).filter(Boolean).join('');
      }

      return children.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
    }

    stringifyMetafieldJson(value) {
      if (value === null || value === undefined) return '';

      if (Array.isArray(value)) {
        return value
          .map((item) => this.stringifyMetafieldJson(item))
          .filter(Boolean)
          .join(', ');
      }

      if (typeof value === 'object') {
        if (this.isRichTextNode(value)) return this.richTextToText(value);
        if (typeof value.value === 'string' || typeof value.value === 'number') return this.cleanValue(value.value);
        if (typeof value.name === 'string') return this.cleanValue(value.name);
        if (typeof value.title === 'string') return this.cleanValue(value.title);
        if (typeof value.label === 'string') return this.cleanValue(value.label);

        return Object.values(value)
          .map((item) => this.stringifyMetafieldJson(item))
          .filter((item) => item && !/^gid:\/\/shopify\//.test(item))
          .join(', ');
      }

      return this.cleanValue(value);
    }

    setStatus(text) {
      if (this.nodes.status) {
        this.nodes.status.textContent = text || '';
        this.nodes.status.hidden = !text;
      }
    }

    async storefrontRequest(query, variables) {
      const endpoint = `https://${this.storeDomain.replace(/^https?:\/\//, '').replace(/\/$/, '')}/api/2025-10/graphql.json`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query, variables })
      });
      const payload = await response.json();
      if (!response.ok || payload.errors?.length) {
        throw new Error(payload.errors?.[0]?.message || response.statusText);
      }
      return payload;
    }

    productFields() {
      return `
        id
        title
        handle
        vendor
        productType
        tags
        onlineStoreUrl
        aggregateProduct: metafield(namespace: "custom", key: "aggregate_product") {
          value
        }
        featuredImage {
          url(transform: { maxWidth: 640 })
          altText
          width
          height
        }
        ${this.metafieldRefs.map((ref) => this.metafieldQuery(ref)).join('\n')}
      `;
    }

    metafieldQuery(ref) {
      return `
        ${ref.alias}: metafield(namespace: "${this.escapeGraphqlString(ref.namespace)}", key: "${this.escapeGraphqlString(ref.key)}") {
          type
          value
          reference {
            ... on Metaobject {
              id
              handle
              fields {
                key
                value
              }
            }
          }
          references(first: 10) {
            nodes {
              ... on Metaobject {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;
    }

    escapeGraphqlString(value) {
      return this.cleanValue(value)
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');
    }

    metaobjectsQuery() {
      return `
        query ProductCenterMetaobjects($type: String!, $first: Int!, $after: String) {
          metaobjects(type: $type, first: $first, after: $after) {
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;
    }

    productsQuery() {
      return `
        query ProductCenterAllProducts($first: Int!, $after: String) {
          products(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                ${this.productFields()}
              }
            }
          }
        }
      `;
    }

    productSearchQuery() {
      return `
        query ProductCenterSearchProducts($first: Int!, $after: String, $query: String!) {
          products(first: $first, after: $after, query: $query) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                ${this.productFields()}
              }
            }
          }
        }
      `;
    }

    collectionProductsQuery() {
      return `
        query ProductCenterProducts($handle: String!, $first: Int!, $after: String) {
          collection(handle: $handle) {
            products(first: $first, after: $after) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  ${this.productFields()}
                }
              }
            }
          }
        }
      `;
    }

    cleanValue(value) {
      return String(value || '').trim();
    }

    normalize(value) {
      return this.cleanValue(value).toLowerCase().replace(/\s+/g, ' ');
    }

    normalizeUrlToken(value) {
      return this.normalize(value).replace(/[-_]+/g, ' ');
    }

    escapeHtml(value) {
      return this.cleanValue(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  customElements.define('product-center', ProductCenter);
}
